/**
 * AddJobDialog.js
 *
 * A dialog/modal for adding jobs to NDORFlow in two modes:
 *   Mode S — Smart URL (fetch + NDOR-parse into structured fields)
 *   Mode D — Manual entry (paste the description / fill the fields)
 *
 * RC 1.49.x Stabilisation Slice 1 (2026-05-07) — operator-locked:
 *   1. Three-mode UI collapsed to two. The legacy "Paste link" tab
 *      (which only stored the URL) was redundant against Smart URL +
 *      Manual entry, so it has been removed entirely. No more
 *      manual-link calls from this dialog. The backend
 *      endpoint stays in place (untouched) for any external callers.
 *   2. Form state (active mode + every typed field + the
 *      blocked-site auto-switch flag + the open flag) is mirrored to
 *      sessionStorage under `ndor.addJobDialog.v1` so a browser tab
 *      switch / accidental remount can't wipe in-flight typing.
 *   3. The DialogContent is guarded against pointer-down-outside +
 *      interact-outside dismissal so returning from another browser
 *      tab and clicking on the underlying page can't silently close
 *      the modal. Escape, the X, and the explicit Cancel button still
 *      close it.
 *   4. On a successful non-duplicate import in either mode, the user
 *      is navigated to `/jobs/{saved.id}?imported=1` so the just-
 *      imported row gets a confirmation banner with the extracted
 *      summary on JobDetailPage. Duplicates keep the existing
 *      toast-and-stay UX.
 *   5. While `saving` is true the dialog now surfaces a calm rotating
 *      "still working" reassurance message every ~3.5s alongside the
 *      existing 504-recovery banner family. No async backend
 *      architecture: this is purely frontend perceived-latency
 *      polish so the user doesn't think the page is frozen.
 *   6. Generic 5xx / "internal server error" responses are humanised
 *      into a calm, actionable error toast pointing the user at the
 *      manual paste fallback. Targeted backend messages still show
 *      verbatim.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Loader2, FileText, Sparkles, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
// RC 1.59 Slice 4 — close the Add Job → jobsList invalidation gap.
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import { toast } from 'sonner';
import { handleCreditsError } from '@/lib/handleCreditsError';
import { useCredits } from '@/lib/creditsContext';
import { submitIngestUrlWithRecovery } from '@/lib/ingestUrlRecovery';

/** sessionStorage key for the dialog's persisted snapshot. Versioned
 *  so future shape changes don't have to read/migrate old entries. */
const STORAGE_KEY = 'ndor.addJobDialog.v1';

const DEFAULT_FORM = {
  title: '', company: '', location: '', employment_type: '',
  seniority_level: '', description: '', requirements_text: '', url: '',
};

/** RC 1.49.x Slice 1 Part B — progress reassurance copy. The dialog
 *  rotates through these phrases every PROGRESS_TICK_MS while
 *  `saving` is true so the user doesn't think the page is frozen.
 *  Per-mode arrays — Smart URL takes longer (fetch + NDOR LLM),
 *  manual entry is just a Supabase insert.
 *
 *  The rotation is bounded by the array length: once the user
 *  reaches the last phrase we stay on it (no looping back to "step
 *  one" — that would feel like the action restarted). */
const PROGRESS_TICK_MS = 3500;
const PROGRESS_MESSAGES_SMART = [
  'Reading the job posting…',
  'Extracting job details…',
  'Analysing the role…',
  'Still working — preparing your result…',
];
const PROGRESS_MESSAGES_MANUAL = [
  'Saving your job…',
  'Almost done…',
];

const readSnapshot = () => {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
};

const clearSnapshot = () => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
};

const writeSnapshot = (snap) => {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch { /* noop */ }
};

/** RC 1.49.x Slice 1 Part B — humanises generic 5xx / "internal
 *  server error" responses into something the user can act on.
 *  Targeted backend messages (4xx with a real `detail`) pass
 *  through verbatim. Returns a non-empty string for use with
 *  `toast.error()`. */
const humaniseSubmitError = (err, mode) => {
  const raw = (err?.message || '').trim();
  const status = err?.status;
  const looksGeneric = !raw
    || /^internal server error\.?$/i.test(raw)
    || /^server error\.?$/i.test(raw)
    || /^request failed( with status code \d+)?\.?$/i.test(raw);
  if (looksGeneric || (status >= 500 && status < 600)) {
    if (mode === 'smart') {
      return "Something went wrong on our side. Please try again in a moment — or paste the job description directly using Paste description.";
    }
    return 'Something went wrong on our side. Please try again in a moment.';
  }
  return raw || 'Failed to add job.';
};

export default function AddJobDialog({ onJobAdded }) {
  const { refresh: refreshCredits } = useCredits();
  const navigate = useNavigate();
  // RC 1.49.x Slice 1 — hydrate any in-flight typing from a previous
  // mount of this dialog. Only hydrate when the snapshot says the
  // dialog was open; otherwise we'd silently re-pop a closed modal.
  const initial = readSnapshot();
  const hadOpenSnapshot = Boolean(initial?.open);
  const [open, setOpen] = useState(hadOpenSnapshot);
  // Mode is now constrained to 'smart' | 'description'. Any legacy
  // `'link'` snapshot from a previous build falls back to 'smart'.
  const [mode, setMode] = useState(() => {
    if (!hadOpenSnapshot) return 'smart';
    const m = initial?.mode;
    return m === 'description' ? 'description' : 'smart';
  });
  const [saving, setSaving] = useState(false);

  // Smart URL mode fields
  const [smartUrl, setSmartUrl] = useState(
    hadOpenSnapshot ? (initial.smartUrl || '') : '',
  );
  const [smartRawText, setSmartRawText] = useState(
    hadOpenSnapshot ? (initial.smartRawText || '') : '',
  );
  const [smartNeedsFallback, setSmartNeedsFallback] = useState(false);
  // RC 1.49.x Phase 3B — blocked-site auto-switch flag.
  // When the smart-URL ingest returns a 422 indicating the remote
  // site blocks automated access, the dialog auto-switches the user
  // to the manual-entry (description) tab and surfaces a calm
  // explainer banner above the form. The original URL is preserved
  // in `form.url` so the user can reference / paste it back later.
  const [blockedSiteAutoSwitched, setBlockedSiteAutoSwitched] = useState(false);
  // Hydrate the flag from the snapshot on first render (separate
  // from the literal `useState(false)` above so the existing
  // RC 1.49.x Phase 3B contract test keeps pinning the default).
  useEffect(() => {
    if (hadOpenSnapshot && initial?.blockedSiteAutoSwitched) {
      setBlockedSiteAutoSwitched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // RC 1.49.1 P0-1 — recovery banner state for the 30s ingress 504
  // path. `idle` (default) | `checking` (post-504, polling for a
  // saved row) | `recovered` (we found one) | `unknown`
  // (recovery exhausted; user should refresh and check the list).
  const [recoveryState, setRecoveryState] = useState('idle');

  // Description mode fields
  const [form, setForm] = useState(
    hadOpenSnapshot && initial.form && typeof initial.form === 'object'
      ? { ...DEFAULT_FORM, ...initial.form }
      : { ...DEFAULT_FORM },
  );

  // RC 1.49.x Slice 1 Part B — progress reassurance ticker. While
  // `saving` is true a tick increments every PROGRESS_TICK_MS and
  // drives the rotating "still working" copy. Reset whenever
  // saving flips back to false.
  const [progressTick, setProgressTick] = useState(0);
  const progressTimerRef = useRef(null);
  useEffect(() => {
    if (!saving) {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setProgressTick(0);
      return undefined;
    }
    setProgressTick(0);
    progressTimerRef.current = setInterval(() => {
      setProgressTick((t) => t + 1);
    }, PROGRESS_TICK_MS);
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, [saving]);
  const progressMessages = mode === 'smart'
    ? PROGRESS_MESSAGES_SMART
    : PROGRESS_MESSAGES_MANUAL;
  const progressMessage = saving
    ? progressMessages[Math.min(progressTick, progressMessages.length - 1)]
    : null;

  // RC 1.49.x Slice 1 — mirror typed values to sessionStorage while
  // the dialog is open. We deliberately skip the `saving` flag and
  // the transient `recoveryState` / `smartNeedsFallback` — those
  // recover from server signals on next submit, not from disk.
  useEffect(() => {
    if (!open) {
      // Closed → clear any prior snapshot so a future mount doesn't
      // resurrect a stale dialog.
      clearSnapshot();
      return;
    }
    writeSnapshot({
      open,
      mode,
      smartUrl,
      smartRawText,
      blockedSiteAutoSwitched,
      form,
    });
  }, [
    open, mode, smartUrl, smartRawText, blockedSiteAutoSwitched, form,
  ]);

  const resetForm = () => {
    setSmartUrl(''); setSmartRawText(''); setSmartNeedsFallback(false);
    setBlockedSiteAutoSwitched(false);
    setForm({ ...DEFAULT_FORM });
    setMode('smart');
    setRecoveryState('idle');
    clearSnapshot();
  };

  // RC 1.49.1 P0-1 — wraps the smart-URL POST in a 504/timeout
  // recovery loop. Implementation lives in `lib/ingestUrlRecovery`
  // for hermetic Jest pinning (`rc_1_49_1_p0_1_url_import_recovery.test.js`).
  const submitSmartIngestWithRecovery = (body) =>
    submitIngestUrlWithRecovery(body, { onState: setRecoveryState });

  const handleSubmit = async () => {
    // Validate
    if (mode === 'smart' && !smartUrl.trim()) { toast.error('Please paste a job URL.'); return; }
    if (mode === 'description' && !form.title.trim()) { toast.error('Job title is required.'); return; }
    if (mode === 'description' && !form.description.trim()) { toast.error('Job description is required.'); return; }

    setSaving(true);
    setRecoveryState('idle');
    try {
      let saved;
      if (mode === 'smart') {
        const body = { url: smartUrl.trim() };
        if (smartRawText.trim()) body.raw_text = smartRawText.trim();
        saved = await submitSmartIngestWithRecovery(body);
      } else {
        const body = { ...form };
        Object.keys(body).forEach(k => { if (!body[k]) delete body[k]; });
        saved = await apiFetch('/api/jobs/manual-description', { method: 'POST', body });
      }
      if (saved?.duplicate) {
        toast.info('This job is already in your list — opened the existing entry.');
      } else {
        toast.success('Job added successfully.');
        if (mode === 'smart') refreshCredits();
      }
      // RC 1.59 Slice 4 — close the Add Job → jobsList invalidation
      // gap so the freshly added row is visible on the next Jobs
      // page mount / dashboard tile re-derivation without waiting
      // for the 3-minute TTL. Sweep covers both the parametric
      // `jobs:list:*` keys and the dashboard bundle. Fail-open.
      try { cacheInvalidate(['jobs', 'dashboard:bundle']); }
      catch { /* noop */ }
      // RC 1.49.x Slice 1 — capture the saved id BEFORE we close /
      // reset, so the navigate call below sees the right row.
      const savedId = saved?.id || null;
      const isDuplicate = Boolean(saved?.duplicate);
      setOpen(false);
      resetForm();
      onJobAdded?.();
      // Route to the new job's detail page so the user immediately
      // sees what was extracted. Operator-locked: only on success
      // AND only when the row is fresh (duplicates keep the legacy
      // close-and-stay UX so the toast plus existing list are the
      // user's signal).
      if (savedId && !isDuplicate) {
        navigate(`/jobs/${savedId}?imported=1`);
      }
    } catch (err) {
      if (handleCreditsError(err, { refreshCredits, action: 'job URL import' })) {
        // credits error already surfaced via shared toast; noop.
      } else if (
        mode === 'smart'
        && (
          err?.status === 422
          || /blocks automated|automated access|paste the (description|job)/i.test(err?.message || '')
        )
      ) {
        // RC 1.49.x Phase 3B — blocked-site graceful fallback.
        // The remote site (LinkedIn / Indeed / JobStreet et al.)
        // refuses automated fetching. Don't shout at the user with
        // a red error toast — calmly switch them to the manual-entry
        // tab, preserve the URL they pasted, and surface an
        // explainer banner. NO retry, NO scrape workaround, NO
        // credit charge (this branch never reaches a charging path).
        setForm((f) => ({ ...f, url: smartUrl.trim() }));
        setBlockedSiteAutoSwitched(true);
        setMode('description');
        setSmartNeedsFallback(false);
      } else if (mode === 'smart' && recoveryState === 'unknown') {
        // Recovery loop exhausted. Banner is rendered inline; do
        // NOT also fire a toast (would compete for attention).
      } else {
        // RC 1.49.x Slice 1 Part B — humanise generic 5xx /
        // "internal server error" so the user sees something
        // actionable instead of a raw stack trace string.
        toast.error(humaniseSubmitError(err, mode));
      }
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (field) => (e) => setForm({ ...form, [field]: e?.target?.value ?? e });

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
      <DialogTrigger asChild>
        <Button data-testid="add-job-button"><Plus className="h-4 w-4 mr-1.5" /> Add Job</Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="add-job-dialog-content"
        // RC 1.49.x Slice 1 — operator-locked dismissal guard.
        // Returning from another browser tab almost always involves
        // a pointer-down somewhere on the page; without these guards
        // Radix dismisses the dialog and wipes typed state.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-ndor-ink font-outfit">Add a new job</DialogTitle>
        </DialogHeader>

        {/* Mode tabs — two only (Smart URL · Paste description). */}
        <div className="flex gap-2 mt-2 mb-4 flex-wrap">
          <button
            type="button"
            onClick={() => { setMode('smart'); setBlockedSiteAutoSwitched(false); }}
            data-testid="add-job-mode-smart"
            disabled={saving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${mode === 'smart' ? 'bg-ndor-surface-alt text-ndor-ink' : 'text-ndor-ink-muted hover:text-ndor-ink'}`}
          >
            <Sparkles className="h-4 w-4" /> Smart URL
          </button>
          <button
            type="button"
            onClick={() => setMode('description')}
            data-testid="add-job-mode-description"
            disabled={saving}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${mode === 'description' ? 'bg-ndor-surface-alt text-ndor-ink' : 'text-ndor-ink-muted hover:text-ndor-ink'}`}
          >
            <FileText className="h-4 w-4" /> Paste description
          </button>
        </div>

        {/* Smart URL mode — fetch + NDOR-parse into structured fields */}
        {mode === 'smart' && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Job URL *</Label>
              <Input
                value={smartUrl}
                onChange={(e) => { setSmartUrl(e.target.value); setSmartNeedsFallback(false); setRecoveryState('idle'); }}
                placeholder="https://..."
                className="h-10"
                data-testid="add-job-smart-url"
                disabled={saving}
              />
              <p className="text-xs text-ndor-ink-muted" data-testid="add-job-smart-helper">
                Paste a job posting link and we'll try to extract the details automatically.
              </p>
            </div>

            {/* RC 1.49.1 P0-1 — persistent recovery banner. Shown when
                the initial POST 504s and we're polling for a saved
                row (`checking`), or when the recovery loop has
                exhausted (`unknown`). Persists across renders so
                the user can read it instead of chasing a toast. */}
            {recoveryState === 'checking' && (
              <div
                role="status"
                data-testid="add-job-recovery-banner-checking"
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink flex items-start gap-2"
              >
                <Loader2 className="h-4 w-4 animate-spin shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">This is taking longer than usual.</div>
                  <div className="text-ndor-ink-muted">
                    The page may have been saved already — we're checking. Please don't close this dialog yet.
                  </div>
                </div>
              </div>
            )}
            {recoveryState === 'unknown' && (
              <div
                role="alert"
                data-testid="add-job-recovery-banner-unknown"
                className="rounded-md border border-amber-300 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-start gap-2"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">We couldn't confirm the import.</div>
                  <div>
                    The connection timed out, but the job may still have saved. Close this dialog and refresh your Jobs list to check before trying again. If it isn't there, paste the description below.
                  </div>
                </div>
              </div>
            )}

            {(smartNeedsFallback || recoveryState === 'unknown') && (
              <div className="space-y-1.5" data-testid="add-job-smart-fallback-block">
                <Label className="text-ndor-ink">Paste the job description instead</Label>
                <Textarea
                  value={smartRawText}
                  onChange={(e) => setSmartRawText(e.target.value)}
                  placeholder="Some sites (LinkedIn, Indeed) block automated fetching. Paste the job text here and we'll still structure it."
                  rows={6}
                  data-testid="add-job-smart-raw-text"
                  disabled={saving}
                />
                {smartNeedsFallback && (
                  <p className="text-xs text-ndor-warning" data-testid="add-job-smart-fallback-hint">
                    We couldn't read that URL automatically. Paste the description above and we'll structure it for you.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Description mode */}
        {mode === 'description' && (
          <div className="space-y-4">
            {/* RC 1.49.x Slice 1 — manual-mode helper line. Renders
                whether the user reached this tab voluntarily OR via
                the blocked-site auto-switch — the explainer banner
                below adds the auto-switch context when relevant. */}
            <p
              className="text-xs text-ndor-ink-muted"
              data-testid="add-job-manual-helper"
            >
              If automatic import is blocked or incomplete, paste the job description here and add the job details manually.
            </p>
            {/* RC 1.49.x Phase 3B — blocked-site auto-switch explainer.
                Renders only when the user landed on this tab via the
                smart-URL 422-blocked-site fallback. Calm tone, no red
                error styling. The URL they pasted is preserved at the
                bottom of the form (Source URL field). */}
            {blockedSiteAutoSwitched && (
              <div
                role="status"
                data-testid="add-job-blocked-site-banner"
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5 text-xs text-ndor-ink-secondary flex items-start gap-2 leading-relaxed"
              >
                <AlertCircle className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-ndor-ink">
                    This site appears to block automated imports.
                  </div>
                  <div>
                    You can still add the role manually below. Your
                    original URL is preserved in the Source URL field
                    and won't be charged or re-imported.
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-ndor-ink">Title *</Label>
                <Input value={form.title} onChange={updateForm('title')} placeholder="Senior Engineer" className="h-10" data-testid="add-job-title" disabled={saving} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-ndor-ink">Company</Label>
                <Input value={form.company} onChange={updateForm('company')} placeholder="Acme Inc." className="h-10" disabled={saving} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-ndor-ink-secondary text-xs">Location</Label>
                <Input value={form.location} onChange={updateForm('location')} placeholder="London, UK" className="h-9" disabled={saving} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-ndor-ink-secondary text-xs">Employment type</Label>
                <Select value={form.employment_type} onValueChange={updateForm('employment_type')} disabled={saving}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['full_time', 'part_time', 'contract', 'freelance', 'internship'].map(t => (
                      <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink-secondary text-xs">Seniority level</Label>
              <Select value={form.seniority_level} onValueChange={updateForm('seniority_level')} disabled={saving}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {['junior', 'mid', 'senior', 'lead', 'manager', 'director', 'executive'].map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink">Description *</Label>
              <Textarea value={form.description} onChange={updateForm('description')} placeholder="Paste the full job description..." rows={5} data-testid="add-job-description" disabled={saving} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink-secondary text-xs">Requirements (optional)</Label>
              <Textarea value={form.requirements_text} onChange={updateForm('requirements_text')} placeholder="Requirements, qualifications..." rows={3} disabled={saving} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-ndor-ink-secondary text-xs">Source URL (optional)</Label>
              <Input value={form.url} onChange={updateForm('url')} placeholder="https://..." className="h-9" disabled={saving} />
            </div>
          </div>
        )}

        {/* RC 1.49.x Slice 1 Part B — calm "still working" reassurance
            row. Persists for the duration of the submit so the user
            doesn't suspect a frozen page. Targeted single line; the
            pre-existing 504 recovery banners take over for the
            slow-path / timeout case. */}
        {progressMessage && (
          <div
            role="status"
            aria-live="polite"
            data-testid="add-job-progress"
            className="mt-4 flex items-center gap-2 text-xs text-ndor-ink-secondary"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-ndor-ink-muted" />
            <span data-testid="add-job-progress-message">{progressMessage}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-ndor-edge">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving} data-testid="add-job-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="add-job-submit">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {saving
              ? (mode === 'smart' ? 'Extracting…' : 'Saving…')
              : (mode === 'smart' ? 'Extract & add' : 'Add Job')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
